import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import i18next from "i18next";
import { Dropdown, Row } from "react-bootstrap";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { InfoBanner, ModalDelete, Spinner, SpinnerLoader, TableHeader } from "@services/ui-components";
import { useNavigate, useParams } from "react-router-dom";
import UpdateRecoveryProductForm from "./UpdateRecoveryProductForm";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useDeleteProductsMutation, useGetOneProductQuery } from "@me-team/host/main/products/graphql/products.hooks";
import ErrorService from "../../../services/ErrorService";
import { CompanyAccess, ProductProxy } from "@me-team/host/main/graphql/types";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { DeepPartial } from "@apollo/client/utilities";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const UpdateProduct: React.FC = () => {
    const { t } = useTranslation();
    const { id, branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canDeleteRestoreProduct = useRoleAccess(CompanyAccess.ProductDeleteRestore);

    const [showDropdownActions, setShowDropdownActions] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showCannotDelete, setShowCannotDelete] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [deleteProductId, setDeleteProductId] = useState<number | null>(null);

    const { data: productOneData, refetch: refetchProduct } = useGetOneProductQuery({
        skip: !currentCompanyId || !branchId,
        variables: {
            companyId: Number(currentCompanyId),
            id: +id,
            branchId: Number(branchId),
            needsFilterByBranch: false,
        },
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const product: DeepPartial<ProductProxy> = productOneData?.products?.items[0];

    const [deleteProducts] = useDeleteProductsMutation({
        onCompleted: async () => {
            setShowDeleteConfirmation(false);
            await refetchProduct();
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const handleToggleDropdownActions = () => setShowDropdownActions(!showDropdownActions);
    const handleCannotDeleteClose = () => setShowCannotDelete(false);
    const handleConfirmationClose = () => setShowDeleteConfirmation(false);

    const handleShowModalDelete = () => {
        if (!canDeleteRestoreProduct) return;

        setDeleteProductId(product?.id || null);
        const isAvailability = product?.productStockStatus === "In stock";
        if (isAvailability) {
            setShowCannotDelete(true);
        } else {
            setShowDeleteConfirmation(true);
        }
    };

    const handleDelete = async () => {
        if (!canDeleteRestoreProduct) return;
        if (!deleteProductId) return;
        setIsLoading(true);
        await deleteProducts({
            variables: { productIds: [deleteProductId], branchId: +branchId },
            context: { errorType: "local" },
        });
        setIsLoading(false);
        navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`, {
            state: { text: t("Deleted"), toast: true },
        });
    };

    if (!currentCompanyId) return <Spinner />;
    return (
        <div className="mb-3">
            <InfoBanner>
                <Trans
                    i18nKey="Required fields are marked with an asterisk -"
                    components={{ red: <span className="text-danger">*</span> }}
                />
            </InfoBanner>
            <TableHeader
                title={t("Edit Product")}
                paths={[
                    {
                        path: `/warehouse/product/${currentCompanyId}/${id}/edit-product/branch/${branchId}`,
                        label: t("Warehouse" + " accounting"),
                    },
                    { path: `/warehouse/product/${currentCompanyId}/branch/${branchId}`, label: t("Products") },
                    {
                        path: `/warehouse/product/${currentCompanyId}/${id}/edit-product/branch/${branchId}`,
                        label: t("Edit" + " Product"),
                    },
                ]}
                colSize={7}
                btnColSize={5}
                buttons={
                    canDeleteRestoreProduct ? (
                        <div className="d-flex justify-content-end mt-4 mt-lg-0">
                            <div className="col-12 col-sm-4 col-xl-4 col-xxl-3">
                                <Dropdown
                                    className="dropdown-action"
                                    show={showDropdownActions}
                                    onToggle={handleToggleDropdownActions}
                                    drop={"down-centered"}
                                >
                                    <Dropdown.Toggle variant="outline-primary" id="actionsDropdown" className="w-100">
                                        <div className="d-flex justify-content-between">
                                            <span className="me-2">{t("Actions")}</span>
                                            <i
                                                className={`bi bi-chevron-down transition-icon ${showDropdownActions ? "rotate-up" : ""}`}
                                            ></i>
                                        </div>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item className="py-2" onClick={() => handleShowModalDelete()}>
                                            <i className="bi bi-trash3 me-2"></i>
                                            {t("Remove")}
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    ) : null
                }
            />
            <Row>
                <UpdateRecoveryProductForm />
            </Row>

            <ModalDelete
                show={showDeleteConfirmation}
                handleClose={handleConfirmationClose}
                title={t("Deleting of product(s)")}
                body={"Are you sure you want to delete selected product(s)?"}
                buttons={[
                    {
                        text: t("Cancel"),
                        onClick: handleConfirmationClose,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                    },
                    {
                        text: isLoading ? (
                            <SpinnerLoader variant="light" loadingText={t("Deleting...")} />
                        ) : (
                            t("Delete")
                        ),
                        onClick: handleDelete,
                        variant: "primary",
                        className: "w-100",
                        disabled: isLoading || !canDeleteRestoreProduct,
                    },
                ]}
            />
            <ModalDelete
                show={showCannotDelete}
                handleClose={handleCannotDeleteClose}
                title={t("It is impossible to remove the product")}
                variantTitle="text-danger"
                body={t(
                    "This product cannot be deleted. Please write off or migrate the selected product from the warehouse, before deleting."
                )}
                buttons={[
                    {
                        text: t("okBtn"),
                        onClick: handleCannotDeleteClose,
                        variant: "danger",
                        className: "w-100",
                    },
                ]}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default UpdateProduct;
